import { useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  searchApp,
  SearchOptions,
  SearchRateLimitedError,
  SearchResponse,
} from "./appSearchApi";

/** Matches the server's `chars().count()` gate (code points, not UTF-16 units). */
export const MIN_QUERY_CODE_POINTS = 3;
const MAX_QUERY_CODE_POINTS = 256;
const QUERY_DEBOUNCE_MS = 150;

export interface UseAppSearch {
  /** Last successful response; kept while a newer search is in flight. */
  results: SearchResponse | null;
  isSearching: boolean;
  /** Non-transient failures only — 429 silently keeps stale results. */
  error: string | null;
}

export const codePointLength = (value: string): number =>
  Array.from(value).length;

const clampToMaxCodePoints = (value: string): string =>
  Array.from(value).slice(0, MAX_QUERY_CODE_POINTS).join("");

export const useAppSearch = (
  query: string,
  options?: SearchOptions,
): UseAppSearch => {
  const importInto = options?.importInto;
  const [results, setResults] = useState<SearchResponse | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const controllerRef = useRef<AbortController | null>(null);
  const requestSeqRef = useRef(0);

  useEffect(() => {
    const trimmed = query.trim();
    if (codePointLength(trimmed) < MIN_QUERY_CODE_POINTS) {
      controllerRef.current?.abort();
      controllerRef.current = null;
      requestSeqRef.current += 1;
      setResults(null);
      setIsSearching(false);
      setError(null);
      return;
    }

    const handle = setTimeout(() => {
      controllerRef.current?.abort();
      const controller = new AbortController();
      controllerRef.current = controller;
      const seq = (requestSeqRef.current += 1);
      const isLatest = () => seq === requestSeqRef.current;

      setIsSearching(true);
      searchApp(clampToMaxCodePoints(trimmed), controller.signal, {
        importInto,
      })
        .then((response) => {
          if (!isLatest()) return;
          setResults(response);
          setError(null);
          setIsSearching(false);
        })
        .catch((err: unknown) => {
          if (!isLatest() || axios.isCancel(err)) return;
          if (err instanceof SearchRateLimitedError) {
            setIsSearching(false);
            return;
          }
          setError("Search is unavailable right now");
          setIsSearching(false);
        });
    }, QUERY_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [query, importInto]);

  useEffect(
    () => () => {
      controllerRef.current?.abort();
    },
    [],
  );

  return { results, isSearching, error };
};
